import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, filter, tap, catchError, finalize, throwError, from, switchMap } from 'rxjs';
import { LoaderService } from './loader.service';
import { NotificationService } from './notification.service';
import { environment } from '../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { ErrorHandler } from './error-handler';
import { LocalstorageService } from './localstorage.service';
import { Router } from '@angular/router';
import { LoginResponse } from '../B2B/onboarding/onboarding.interface';

const baseUrl = environment.apiUrl;

export interface RequestOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: HttpParams;
  /** The screen shows the error itself (inline); no toast. */
  skipErrorNotification?: boolean;
  /** What the request was doing, shown in front of the API's reason: "Couldn't load fees: <reason>". */
  errorContext?: string;
  /** Show the API's `message` as a success toast. */
  showSuccessNotification?: boolean;
  /** Success toast to show when the API sends no `message` of its own. */
  successMessage?: string;
  skipAuth?: boolean;
  payloadType?: 'json' | 'form-data' | 'text' | 'blob' | string;
  responseType?: 'json' | 'blob' | 'text' | 'arraybuffer';
}

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  storedUserObject: LoginResponse;

  constructor(
    private http: HttpClient,
    private notify: NotificationService,
    private loader: LoaderService,
    private localStorageService: LocalstorageService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.storedUserObject = this.localStorageService.get('userObject');
  }

  private createOptions(options: RequestOptions = {}, payloadType: string): any {
    let headers = options.headers instanceof HttpHeaders ? options.headers : new HttpHeaders(options.headers);

    if (options.skipAuth) {
      headers = headers.set('skip-auth', 'true');
    }

    if (payloadType === 'json' && !headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    } else if (payloadType === 'text' && !headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'text/plain');
    } else if (payloadType === 'urlencoded' && !headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');
    }

    const requestOptions: any = {
      headers,
      params: options.params,
    };

    if (options.responseType) {
      requestOptions.responseType = options.responseType;
    } else {
      requestOptions.observe = 'response';
    }

    return requestOptions;
  }

  request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    route: string,
    body?: any,
    options: RequestOptions = {}
  ): Observable<HttpResponse<T>> {
    const url = `${baseUrl}${route}`;
    const payloadType = options.payloadType || (typeof FormData !== 'undefined' && body instanceof FormData ? 'form-data' : 'json');
    const requestOptions = this.createOptions(options, payloadType);

    this.loader.show();

    return this.http.request<T>(method, url, { ...requestOptions, body }).pipe(
      filter((event: HttpEvent<T>): event is HttpResponse<T> => event instanceof HttpResponse),
      tap((res: HttpResponse<T>) => this.handleSuccess(res, options)),
      catchError((error: HttpErrorResponse) => this.handleError(error, options)),
      finalize(() => this.loader.hide())
    );
  }

  /** One success toast: the API's own `message`, else the screen's `successMessage`. */
  private handleSuccess<T>(response: HttpResponse<T>, options: RequestOptions): void {
    if (!isPlatformBrowser(this.platformId) || !(options.showSuccessNotification || options.successMessage)) return;
    const message = (response.body as any)?.message;
    const messages = (Array.isArray(message) ? message : [message]).filter((m) => typeof m === 'string' && m);
    const text = messages.length ? messages.join('\n') : options.successMessage;
    if (text) {
      this.notify.showSuccess(text, 'Success');
    }
  }

  /**
   * One error toast with the API's reason, then the original HttpErrorResponse is passed on so the
   * screen still has the status and field errors. A 401 on a signed-in request is left to the auth
   * interceptor, which refreshes the session or ends it with its own message.
   */
  public handleError(error: any, options: RequestOptions = {}): Observable<never> {
    const sessionHandled = error instanceof HttpErrorResponse && error.status === 401 && !options.skipAuth;
    if (isPlatformBrowser(this.platformId) && !options.skipErrorNotification && !sessionHandled) {
      ErrorHandler.handleError(error, this.notify, undefined, undefined, options.errorContext);
    }
    return throwError(() => error);
  }

  get<T>(route: string, options: RequestOptions = {}): Observable<any> {
    const url = `${baseUrl}${route}`;
    const payloadType = options.payloadType || 'json';
    const requestOptions = this.createOptions(options, payloadType);

    this.loader.show();

    if (options.responseType) {
      // A file request's JSON error arrives as a Blob; read it back so the API's reason is shown.
      return this.http.get<T>(url, requestOptions).pipe(
        catchError((error: HttpErrorResponse) =>
          from(this.readBlobError(error)).pipe(switchMap((parsed) => this.handleError(parsed, options)))
        ),
        finalize(() => this.loader.hide())
      );
    }

    return this.http.request<T>('GET', url, requestOptions).pipe(
      filter((event: HttpEvent<T>): event is HttpResponse<T> => event instanceof HttpResponse),
      tap((res: HttpResponse<T>) => this.handleSuccess(res, options)),
      catchError((error) => this.handleError(error, options)),
      finalize(() => this.loader.hide())
    );
  }

  post<T>(route: string, body: any, options: RequestOptions = {}): Observable<HttpResponse<T>> {
    return this.request<T>('POST', route, body, options);
  }

  /**
   * POST that answers with a file, e.g. a statement download.
   * A failed request's JSON body arrives as a Blob, so it is parsed back before the error is passed on.
   */
  postBlob(route: string, body: any, options: RequestOptions = {}): Observable<HttpResponse<Blob>> {
    const url = `${baseUrl}${route}`;
    const { headers, params } = this.createOptions(options, options.payloadType || 'json');

    this.loader.show();

    return this.http.post(url, body, { headers, params, observe: 'response', responseType: 'blob' }).pipe(
      catchError((error: HttpErrorResponse) =>
        from(this.readBlobError(error)).pipe(switchMap((parsed) => this.handleError(parsed, options)))
      ),
      finalize(() => this.loader.hide())
    );
  }

  private async readBlobError(error: HttpErrorResponse): Promise<HttpErrorResponse> {
    if (!(error.error instanceof Blob)) return error;
    try {
      return new HttpErrorResponse({
        error: JSON.parse(await error.error.text()),
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined,
      });
    } catch {
      return error;
    }
  }

  put<T>(route: string, body: any, options: RequestOptions = {}): Observable<HttpResponse<T>> {
    return this.request<T>('PUT', route, body, options);
  }

  /**
   * PATCH request wrapper.
   * Mirrors put() but uses the PATCH HTTP method via http.patch() directly
   * so Angular's HttpClient emits the full HttpResponse (observe: 'response').
   *
   * Usage with form-urlencoded (e.g. Django REST):
   *   const body = new HttpParams().set('active', 'true');
   *   this.httpService.patch('onboarding/merchant/38', body, {
   *     payloadType: 'urlencoded'
   *   });
   */
  patch<T>(route: string, body: any, options: RequestOptions = {}): Observable<HttpResponse<T>> {
    const url = `${baseUrl}${route}`;
    const payloadType = options.payloadType || (typeof FormData !== 'undefined' && body instanceof FormData ? 'form-data' : 'json');
    const requestOptions = this.createOptions(options, payloadType);

    this.loader.show();

    return this.http.patch<T>(url, body, requestOptions).pipe(
      filter((event: HttpEvent<T>): event is HttpResponse<T> => event instanceof HttpResponse),
      tap((res: HttpResponse<T>) => this.handleSuccess(res, options)),
      catchError((error: HttpErrorResponse) => this.handleError(error, options)),
      finalize(() => this.loader.hide())
    );
  }

  delete<T>(route: string, options: RequestOptions = {}): Observable<HttpResponse<T>> {
    return this.request<T>('DELETE', route, null, options);
  }

  getWithoutToken<T>(route: string): Observable<T> {
    return this.http.get<T>(`${baseUrl}${route}`, {
      headers: new HttpHeaders({ 'skip-auth': 'true' }),
    });
  }

  postWithoutToken<T>(route: string, payload: any): Observable<T> {
    return this.http.post<T>(`${baseUrl}${route}`, payload, {
      headers: new HttpHeaders({ 'skip-auth': 'true' }),
    });
  }
}